/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, October 5th 2021, 7:38:48 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import TableHead from "@mui/material/TableHead";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Checkbox from "@mui/material/Checkbox";
import { visuallyHidden } from "@mui/utils";
import { Key, ObjectKey, SortDirection } from "lib/types";
import Box from "@mui/material/Box";

export interface HeadCell<OSObjectType, CustomKeys = null> {
  id: ObjectKey<OSObjectType> | CustomKeys;
  numeric: boolean;
  disablePadding: boolean;
  label: string;
  ActionComponent?: React.ComponentType<any>;
}

export interface DataTableHeadProps<OSObjectType> {
  numSelected: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: ObjectKey<OSObjectType>) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: SortDirection;
  orderBy: ObjectKey<OSObjectType>;
  rowCount: number;
  headCells: readonly HeadCell<OSObjectType>[];
  allowSelect: boolean;
  allRowsSelected?: boolean;
  showAvatar: boolean;
  enableRowItemActions?: boolean;
}

export const DataTableHead = <OSObjectType,>(props: DataTableHeadProps<OSObjectType>) => {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    headCells,
    allowSelect,
    allRowsSelected = false,
    showAvatar,
    enableRowItemActions,
  } = props;
  const createSortHandler = (property: ObjectKey<OSObjectType>) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {allowSelect ? (
          <TableCell padding="checkbox">
            <Checkbox
              color="primary"
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={allRowsSelected}
              onChange={onSelectAllClick}
              inputProps={{
                "aria-label": "select all items",
              }}
            />
          </TableCell>
        ) : (
          <TableCell padding="checkbox" />
        )}
        {showAvatar ? <TableCell padding="normal" /> : null}
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id as Key}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
        {enableRowItemActions ? <TableCell padding="none">Actions</TableCell> : null}
      </TableRow>
    </TableHead>
  );
};
